import { Card } from '@etg/wings';
import { Accordion, ConditionalWrapper } from '@eti/components';
import { css } from '@eti/styles';
import type { Dispatch, SetStateAction } from 'react';
import { useMemo } from 'react';
import { TripType } from '@eti/schema-types';
import BoundTitle from '../common/BoundTitle';
import type { OriginalVersionType } from '../../../../models';
import type { Bound, UpgradeTypePerBound } from '../../../../../../widgets/trip-itinerary/models';
import { transformSegments } from './utils/utils';
import ItineraryBoundHeader from './ItineraryBoundHeader';
import ItineraryEventSegment from './ItineraryEventSegment';
import ItineraryTripSegment from './ItineraryTripSegment';

const wrapperStyles = css`
  padding-top: 8px;
`;

interface ItineraryBoundProps {
  boundId: string;
  boundIndex: number;
  isSelected?: boolean;
  journeyType: TripType;
  originalVersion?: OriginalVersionType | null;
  segments: Bound['segments'];
  setIsItineraryDrawerSelfTransferModalOpen?: Dispatch<SetStateAction<boolean>>;
  segmentsCabinUpgradePrices?: UpgradeTypePerBound[] | null;
}

const ItineraryBound = ({
  boundId,
  boundIndex,
  isSelected,
  journeyType,
  originalVersion,
  segments,
  setIsItineraryDrawerSelfTransferModalOpen,
  segmentsCabinUpgradePrices,
}: ItineraryBoundProps) => {
  const transformedSegments = useMemo(() => transformSegments(segments), [segments]);
  const heading = (
    <BoundTitle boundId={boundId} boundIndex={boundIndex} journeyType={journeyType} />
  );

  return (
    <Card data-testid={`trip-itinerary-bound-${boundId}`}>
      <ConditionalWrapper
        condition={journeyType !== TripType.OneWay}
        wrapper={(children) => (
          <Accordion
            id={`itinerary-bound-${boundId}-accordion`}
            isInitiallyExpanded={isSelected}
            variant="full-width"
          >
            <Accordion.Header data-testid="itinerary-bound-expansion-panel">
              {heading}
            </Accordion.Header>
            <Accordion.Body>{children}</Accordion.Body>
          </Accordion>
        )}
      >
        <>
          {journeyType === TripType.OneWay && heading}
          <div className={wrapperStyles}>
            <ItineraryBoundHeader
              originalDepartureDate={
                originalVersion ? originalVersion.originalSegments[0].departuredAt : null
              }
              segments={transformedSegments}
            />
            {transformedSegments.map((segment, index) => {
              // TODO: MYP-1758 - Match originalSegment to latestSegment by segmentId
              const originalSegmentIndex = index / 2;
              const segmentCabinUpgrade = segmentsCabinUpgradePrices?.find(
                (bound) => bound.boundIndex === boundIndex && bound.segmentIndex === index,
              );

              switch (segment?.__typename) {
                case 'TripSegment':
                  return (
                    <ItineraryTripSegment
                      isLastSegment={index === segments.length - 1}
                      key={segment.segmentId}
                      originalSegment={
                        originalVersion
                          ? originalVersion.originalSegments[originalSegmentIndex]
                          : null
                      }
                      segment={segment}
                      upgradeType={segmentCabinUpgrade?.upgradeOptions[0].upgradeType}
                    />
                  );
                case 'EventSegment':
                  return (
                    <ItineraryEventSegment
                      key={segment.segmentId}
                      segment={segment}
                      setIsItineraryDrawerSelfTransferModalOpen={
                        setIsItineraryDrawerSelfTransferModalOpen
                      }
                    />
                  );
                default:
                  return null;
              }
            })}
          </div>
        </>
      </ConditionalWrapper>
    </Card>
  );
};

export default ItineraryBound;
